import { Box, Container, Flex, Heading, Text } from "@radix-ui/themes";
import { Helmet } from "react-helmet-async";
import { useLDClient } from "@launchdarkly/react-sdk";
import { IrisCtaLink } from "../../components/IrisCtaLink";
import { TitleBlock } from "../TitleBlock";

export function LearnMorePage() {
  // useLDClient throws during prerendering when LDProvider context doesn't exist
  const ldClient = typeof window !== "undefined" ? useLDClient() : undefined;
  return (
    <Container size="4" className="route-page" maxWidth="840px">
      <Helmet>
        <title>Learn More | Iris</title>
        <meta
          name="description"
          content="The story of Iris — a new kind of AI companion designed to help you understand and befriend your mind."
        />
        <link rel="canonical" href="https://irischat.ai/learn-more" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://irischat.ai/learn-more" />
        <meta property="og:title" content="Learn More | Iris" />
        <meta
          property="og:description"
          content="The story of Iris — a new kind of AI companion designed to help you understand and befriend your mind."
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Learn More | Iris" />
        <meta
          name="twitter:description"
          content="The story of Iris — a new kind of AI companion designed to help you understand and befriend your mind."
        />
      </Helmet>
      <Flex direction="column" gap="6">
        <TitleBlock title="The Story of Iris" subtitle="Oct 29th, 2025" />

        <Flex direction="column" gap="6" style={{ maxWidth: "560px", margin: "0 auto" }}>
          <Flex direction="column" gap="4" className="route-card">
          <Heading size="8">
            No one is born with an instruction manual for their mind.
          </Heading>
          <Text size="3" className="route-body">
            and yet, it powers everything we do: how we love, create, lead,
            rest, and make sense of the world.
          </Text>
          <Text size="3" className="route-body">
            we learn to use our phones, our computers, our cars… but not our
            thoughts. not our emotions. not the voices that live inside our
            head.
          </Text>
          <Text size="3" className="route-body">
            for all our advances in technology, we still lack one thing: a clear
            way to understand ourselves.
          </Text>
        </Flex>

        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">What if we could change that?</Heading>
            <Text size="3" className="route-body">
              what if we could create a guide that helps you see your own mind;
              not as something to fix, but as something to befriend?
            </Text>
            <Text size="3" className="route-body">
              not a therapist, not a chatbot, but something new: a mirror made
              of empathy and intelligence.
            </Text>
          </Flex>
        </Box>

        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Meet Iris — ai for your mind.</Heading>
            <Text size="3" className="route-body">
              Iris is a private confidant you can text, anytime, about anything.
            </Text>
            <Text size="3" className="route-body">
              she listens. she remembers. she understands.
            </Text>
            <Text size="3" className="route-body">
              she adapts to how you think, helping you explore your thoughts and
              discover new possibilities. she's a companion for your
              consciousness; helping you think, feel, and grow with greater
              awareness.
            </Text>
          </Flex>
        </Box>

        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Built differently.</Heading>
            <Text size="3" className="route-body">
              where others forget, she remembers. where others lose the thread,
              she holds the context. where others feel robotic, she feels real.
            </Text>
            <Text size="3" className="route-body">
              Iris combines memory, context, and emotional intelligence to meet
              you where you are, and grow with you over time.
            </Text>
            <Text size="3" className="route-body">
              she learns your language, your patterns, your hopes. and through
              that understanding, she helps you make sense of your mind, one
              conversation at a time.
            </Text>
          </Flex>
        </Box>

        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Delivered the simplest way possible.</Heading>
            <Text size="3" className="route-body">
              no new app. no logins. Iris lives inside your Messages app; right
              next to the people you care about most.
            </Text>
            <Text size="3" className="route-body">
              because exploring your mind should be as natural as sending a
              text.
            </Text>
          </Flex>
        </Box>

        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Your mind is waiting.</Heading>
            <Text size="3" className="route-body">
              start with a single message.
            </Text>
            <Flex align="center" gap="3" wrap="wrap" justify="center">
              <Box style={{ padding: "20px 0" }}>
                <IrisCtaLink ldClient={ldClient ?? undefined} />
              </Box>
              <Text>
                  Have questions? Contact our friendly support team at support@irischat.ai
              </Text>
              <Text size="1">Proudly created by a Flagship Pioneering company.</Text>
            </Flex>
          </Flex>
        </Box>
        </Flex>
      </Flex>
    </Container>
  );
}
